import React, { useState } from 'react';
import { Send, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Reusable lightweight lead-capture form.
// mode: 'quote' (name, phone, email, issue) | 'callback' (name, phone, besttime)
export const QuickLeadForm = ({
  mode = 'quote',
  source = 'quick-quote',
  dark = false,
  className = '',
  testid = 'quick-lead-form',
}) => {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [f, setF] = useState({ name: '', phone: '', email: '', issue: '', besttime: 'Anytime' });

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const message = mode === 'callback'
      ? `Callback requested. Best time to reach: ${f.besttime}.`
      : (f.issue || 'Requested a quick diagnostic quote.');
    try {
      await axios.post(`${API_URL}/api/leads/contact`, {
        name: f.name,
        email: f.email || undefined,
        phone: f.phone,
        city: '',
        province: '',
        message,
        form_source: source,
      });
      setDone(true);
      toast.success("Got it! Our rebuild team will reach out shortly.");
    } catch (err) {
      console.error('Lead submit failed', err?.response?.data || err);
      toast.error('Something went wrong. Please try again or call us.');
    } finally {
      setLoading(false);
    }
  };

  const labelCls = dark ? 'text-white/80' : 'text-slate-700';
  const inputCls = dark
    ? 'bg-white border-white text-[#003456] placeholder-slate-400 focus:ring-[#CE8946]'
    : 'bg-white border-slate-300 text-[#003456] placeholder-slate-400 focus:ring-[#CE8946]';

  if (done) {
    return (
      <div className={`rounded-2xl p-8 text-center ${dark ? 'bg-white/10' : 'bg-white border border-slate-200'} ${className}`} data-testid={`${testid}-success`}>
        <CheckCircle2 className={`h-12 w-12 mx-auto mb-3 ${dark ? 'text-[#CE8946]' : 'text-[#B87333]'}`} />
        <h4 className={`text-lg font-bold ${dark ? 'text-white' : 'text-[#003456]'}`}>Request received!</h4>
        <p className={`text-sm mt-1 ${dark ? 'text-white/70' : 'text-slate-600'}`}>We'll be in touch shortly.</p>
      </div>
    );
  }

  const field = (k, label, type = 'text', required = false, placeholder = '') => (
    <div>
      <label className={`block text-xs font-medium mb-1.5 ${labelCls}`}>{label}{required && ' *'}</label>
      <input
        type={type}
        value={f[k]}
        onChange={set(k)}
        required={required}
        placeholder={placeholder}
        className={`w-full px-4 py-2.5 rounded-md border text-sm focus:outline-none focus:ring-2 ${inputCls}`}
        data-testid={`${testid}-${k}`}
      />
    </div>
  );

  return (
    <form onSubmit={submit} className={`space-y-3 ${className}`} data-testid={testid}>
      {field('name', 'Your name', 'text', true, 'Full name')}
      {field('phone', 'Phone', 'tel', mode === 'callback', '(555) 123-4567')}
      {mode === 'quote' && field('email', 'Email', 'email', true, 'you@example.com')}
      {mode === 'quote' && (
        <div>
          <label className={`block text-xs font-medium mb-1.5 ${labelCls}`}>What's going wrong?</label>
          <textarea
            value={f.issue}
            onChange={set('issue')}
            rows={2}
            placeholder="e.g. won't pull vacuum, error code, loud pump…"
            className={`w-full px-4 py-2.5 rounded-md border text-sm focus:outline-none focus:ring-2 resize-none ${inputCls}`}
            data-testid={`${testid}-issue`}
          />
        </div>
      )}
      {mode === 'callback' && (
        <div>
          <label className={`block text-xs font-medium mb-1.5 ${labelCls}`}>Best time to reach you</label>
          <select
            value={f.besttime}
            onChange={set('besttime')}
            className={`w-full px-4 py-2.5 rounded-md border text-sm focus:outline-none focus:ring-2 ${inputCls}`}
            data-testid={`${testid}-besttime`}
          >
            <option>Anytime</option>
            <option>Morning</option>
            <option>Afternoon</option>
            <option>Evening</option>
          </select>
        </div>
      )}
      <button
        type="submit"
        disabled={loading}
        className="w-full inline-flex items-center justify-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-6 py-3 rounded-md transition-colors disabled:opacity-60"
        data-testid={`${testid}-submit`}
      >
        {loading ? 'Sending…' : (<>{mode === 'callback' ? 'Request Callback' : 'Get My Quote'} <Send className="h-4 w-4" /></>)}
      </button>
    </form>
  );
};

export default QuickLeadForm;
